import React, { Component, createRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faToggleOn, faToggleOff, faChevronLeft, faChevronRight, faUpload, faTrophy, faSave, faPlus, faUser } from '@fortawesome/free-solid-svg-icons';
import SwalServices from '../../services/swalServices/SwalServices';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';
import { ProgressSpinner } from 'primereact/progressspinner';
import { APIURL } from '../../services/axiosServices/ApiEndPoints';
import moment from 'moment';

export default class EOM extends Component {
    constructor(props) {
        super(props);
        this.swalServices = new SwalServices();
        this.adminServices = new AdminServices();
        this.fileInputRef = createRef();
        this.state = {
            isStatusLoading: false,
            isSavingWinner: false,
            isUploadingImages: false,
            isLoadingWinners: false,
            isDeletingWinner: false,
            isOpen: false,
            // Multiple winners support
            winners: [],
            activeTabIndex: 0,
            selectedWinnerMonth: new Date(),
            currentMonth: moment().format('YYYY-MM'),
            // Current form state (for active tab)
            currentImageIndex: 0
        };
    }

    componentDidMount() {
        this.getEOMStatus();
        this.loadWinnersForMonth();
    }

    getEmptyWinner = () => ({
        id: null,
        employeename: '',
        roleservicearea: '',
        description: '',
        month: this.state.currentMonth,
        tempImages: [],
        existingImages: []
    });

    loadWinnersForMonth = async () => {
        this.setState({ isLoadingWinners: true });
        try {
            const response = await this.adminServices.getEOMWinner(this.state.currentMonth);
            if (response?.success === 1 && response.data) {
                // Handle both single winner (object) and multiple winners (array)
                const winnersData = Array.isArray(response.data) ? response.data : [response.data];

                if (winnersData.length > 0 && winnersData[0]) {
                    const winners = winnersData.map(winner => ({
                        id: winner.id,
                        employeename: winner.employeename || '',
                        roleservicearea: winner.roleservicearea || '',
                        description: winner.description || '',
                        month: winner.month,
                        tempImages: [],
                        existingImages: winner.images || []
                    }));
                    this.setState({
                        winners,
                        activeTabIndex: 0,
                        currentImageIndex: 0
                    });
                } else {
                    // No winners for this month, start with empty form
                    this.setState({
                        winners: [this.getEmptyWinner()],
                        activeTabIndex: 0,
                        currentImageIndex: 0
                    });
                }
            } else {
                // No winners for this month, start with empty form
                this.setState({
                    winners: [this.getEmptyWinner()],
                    activeTabIndex: 0,
                    currentImageIndex: 0
                });
            }
        } catch (error) {
            this.setState({
                winners: [this.getEmptyWinner()],
                activeTabIndex: 0,
                currentImageIndex: 0
            });
        }
        this.setState({ isLoadingWinners: false });
    };

    getEOMStatus = () => {
        this.setState({ isStatusLoading: true });
        this.adminServices.getEOMStatus().then((response) => {
            if (response?.success === 1) {
                this.setState({ isOpen: response.data.isOpen });
            }
            this.setState({ isStatusLoading: false });
        }).catch(() => this.setState({ isStatusLoading: false }));
    };

    toggleEOMStatus = () => {
        const newStatus = !this.state.isOpen;
        this.setState({ isStatusLoading: true });

        this.adminServices.updateEOMStatus({ isOpen: newStatus }).then((response) => {
            if (response?.success === 1) {
                this.setState({ isOpen: newStatus });
                this.swalServices.Success(response.msg);
            } else {
                this.swalServices.Error(response?.msg || 'Failed to update status');
            }
            this.setState({ isStatusLoading: false });
        }).catch(() => this.setState({ isStatusLoading: false }));
    };

    handleMonthChange = (e) => {
        const selectedDate = e.value;
        if (selectedDate) {
            const newMonth = moment(selectedDate).format('YYYY-MM');
            this.setState({
                selectedWinnerMonth: selectedDate,
                currentMonth: newMonth,
                winners: [],
                activeTabIndex: 0,
                currentImageIndex: 0
            }, () => {
                this.loadWinnersForMonth();
            });
        }
    };

    handleTabChange = (index) => {
        this.setState({
            activeTabIndex: index,
            currentImageIndex: 0
        });
    };

    addNewWinner = () => {
        const newWinner = {
            id: null,
            employeename: '',
            roleservicearea: '',
            description: '',
            month: this.state.currentMonth,
            tempImages: [],
            existingImages: []
        };
        this.setState(prevState => ({
            winners: [...prevState.winners, newWinner],
            activeTabIndex: prevState.winners.length,
            currentImageIndex: 0
        }));
    };

    handleWinnerFormChange = (field, value) => {
        this.setState(prevState => {
            const updatedWinners = [...prevState.winners];
            updatedWinners[prevState.activeTabIndex] = {
                ...updatedWinners[prevState.activeTabIndex],
                [field]: value
            };
            return { winners: updatedWinners };
        });
    };

    handleImageSelect = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        const maxSize = 2 * 1024 * 1024;

        for (const file of files) {
            if (!allowedTypes.includes(file.type)) {
                this.swalServices.Error(`Invalid file type: ${file.name}`);
                return;
            }
            if (file.size > maxSize) {
                this.swalServices.Error(`File too large: ${file.name}`);
                return;
            }
        }

        this.setState({ isUploadingImages: true });

        const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append('image', file);
            try {
                const response = await this.adminServices.uploadTempImage(formData);
                if (response?.success === 1) {
                    return {
                        tempPath: response.data.tempPath,
                        filename: response.data.filename,
                        preview: URL.createObjectURL(file)
                    };
                }
            } catch (error) { }
            return null;
        });

        const results = await Promise.all(uploadPromises);
        const successfulUploads = results.filter(r => r !== null);

        if (successfulUploads.length > 0) {
            this.setState(prevState => {
                const updatedWinners = [...prevState.winners];
                const currentWinner = updatedWinners[prevState.activeTabIndex];
                updatedWinners[prevState.activeTabIndex] = {
                    ...currentWinner,
                    tempImages: [...(currentWinner.tempImages || []), ...successfulUploads]
                };
                return { winners: updatedWinners };
            });
        }

        this.setState({ isUploadingImages: false });
        if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    };

    removeTempImage = (index) => {
        this.setState(prevState => {
            const updatedWinners = [...prevState.winners];
            const currentWinner = updatedWinners[prevState.activeTabIndex];
            const newTempImages = [...currentWinner.tempImages];
            if (newTempImages[index].preview) URL.revokeObjectURL(newTempImages[index].preview);
            newTempImages.splice(index, 1);
            updatedWinners[prevState.activeTabIndex] = {
                ...currentWinner,
                tempImages: newTempImages
            };
            return { winners: updatedWinners, currentImageIndex: 0 };
        });
    };

    removeExistingImage = (index) => {
        this.setState(prevState => {
            const updatedWinners = [...prevState.winners];
            const currentWinner = updatedWinners[prevState.activeTabIndex];
            const newImages = [...currentWinner.existingImages];
            newImages.splice(index, 1);
            updatedWinners[prevState.activeTabIndex] = {
                ...currentWinner,
                existingImages: newImages
            };
            return { winners: updatedWinners, currentImageIndex: 0 };
        });
    };

    saveWinner = async () => {
        const { winners, activeTabIndex, currentMonth } = this.state;
        const currentWinner = winners[activeTabIndex];

        if (!currentWinner.employeename.trim()) {
            this.swalServices.Error('Employee name is required');
            return;
        }
        if (!currentWinner.roleservicearea.trim()) {
            this.swalServices.Error('Role & service area is required');
            return;
        }
        if (!currentWinner.description.trim()) {
            this.swalServices.Error('Description is required');
            return;
        }
        if ((currentWinner.tempImages?.length || 0) === 0 && (currentWinner.existingImages?.length || 0) === 0) {
            this.swalServices.Error('At least one image is required');
            return;
        }

        this.setState({ isSavingWinner: true });

        const payload = {
            employeename: currentWinner.employeename,
            roleservicearea: currentWinner.roleservicearea,
            description: currentWinner.description,
            month: currentMonth,
            tempImages: (currentWinner.tempImages || []).map(img => ({ tempPath: img.tempPath, filename: img.filename })),
            existingImages: currentWinner.existingImages || []
        };

        // Only include id for updates (existing winners)
        if (currentWinner.id) {
            payload.id = currentWinner.id;
        }

        this.adminServices.saveEOMWinner(payload).then((response) => {
            if (response?.success === 1) {
                this.swalServices.Success(response.msg);
                // Clear temp images and reload winner data
                (currentWinner.tempImages || []).forEach(img => {
                    if (img.preview) URL.revokeObjectURL(img.preview);
                });
                this.loadWinnersForMonth();
            } else {
                this.swalServices.Error(response?.msg || 'Failed to save');
            }
            this.setState({ isSavingWinner: false });
        }).catch(() => this.setState({ isSavingWinner: false }));
    };

    deleteWinner = async () => {
        const { winners, activeTabIndex } = this.state;
        const currentWinner = winners[activeTabIndex];

        if (!currentWinner.id) {
            // It's a new unsaved winner, just remove from array
            this.setState(prevState => {
                const updatedWinners = prevState.winners.filter((_, i) => i !== activeTabIndex);
                const newActiveIndex = Math.max(0, activeTabIndex - 1);
                return {
                    winners: updatedWinners.length > 0 ? updatedWinners : [this.getEmptyWinner()],
                    activeTabIndex: updatedWinners.length > 0 ? newActiveIndex : 0,
                    currentImageIndex: 0
                };
            });
            return;
        }

        const confirmed = await this.swalServices.Confirm('Are you sure you want to delete this winner?', '', 'Yes', 'No');
        if (!confirmed) return;

        this.setState({ isDeletingWinner: true });

        this.adminServices.deleteEOMWinner(currentWinner.id).then((response) => {
            if (response?.success === 1) {
                this.swalServices.Success(response.msg);
                this.loadWinnersForMonth();
            } else {
                this.swalServices.Error(response?.msg || 'Failed to delete');
            }
            this.setState({ isDeletingWinner: false });
        }).catch(() => this.setState({ isDeletingWinner: false }));
    };

    getAllImages = () => {
        const { winners, activeTabIndex } = this.state;
        const currentWinner = winners[activeTabIndex];
        if (!currentWinner) return [];

        const all = [];
        (currentWinner.existingImages || []).forEach((img, i) => {
            all.push({ type: 'existing', filename: img, index: i });
        });
        (currentWinner.tempImages || []).forEach((img, i) => {
            all.push({ type: 'temp', preview: img.preview, index: i });
        });
        return all;
    };

    navigateImage = (direction) => {
        const allImages = this.getAllImages();
        if (allImages.length <= 1) return;

        this.setState(prevState => {
            let newIndex = prevState.currentImageIndex + direction;
            if (newIndex < 0) newIndex = allImages.length - 1;
            if (newIndex >= allImages.length) newIndex = 0;
            return { currentImageIndex: newIndex };
        });
    };

    render() {
        const { winners, activeTabIndex, isLoadingWinners } = this.state;
        const currentWinner = winners[activeTabIndex] || this.getEmptyWinner();
        const allImages = this.getAllImages();
        const currentImage = allImages[this.state.currentImageIndex];

        return (
            <div className='eom-page' style={{ height: "calc((100dvh / 0.85) - 28.8px)" }}>
                <div className='eom-inner h-full p-[30px] overflow-y-auto'>
                    {/* Status Toggle Section */}
                    <div className='status-section p-5 bg-white rounded-[16px] mb-[20px]' style={{ border: "1px solid #DFDFDF" }}>
                        <div className='flex items-center justify-between'>
                            <div>
                                <h2 className='text-[16px] font-semibold m-0 mb-1'>Submission Status</h2>
                                <p className='text-sm text-gray-500 m-0'>
                                    {this.state.isOpen ? 'Nominations are currently open on the website' : 'Nominations are currently closed on the website'}
                                </p>
                            </div>
                            <button
                                onClick={this.toggleEOMStatus}
                                disabled={this.state.isStatusLoading}
                                className={`flex items-center gap-2 py-[10px] px-[20px] text-[14px] font-medium rounded-[8px] border-0 ${this.state.isOpen ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'}`}
                            >
                                {this.state.isStatusLoading ? (
                                    <ProgressSpinner style={{ width: '18px', height: '18px' }} strokeWidth="4" />
                                ) : (
                                    <FontAwesomeIcon icon={this.state.isOpen ? faToggleOn : faToggleOff} className='text-xl' />
                                )}
                                {this.state.isOpen ? 'Open' : 'Closed'}
                            </button>
                        </div>
                    </div>

                    {/* Winner Selection Section */}
                    <div className='winner-section p-5 bg-white rounded-[16px]' style={{ border: "1px solid #DFDFDF" }}>
                        <div className='flex items-center gap-3 mb-4'>
                            <FontAwesomeIcon icon={faTrophy} className='text-yellow-500 text-xl' />
                            <h2 className='text-[16px] font-semibold m-0'>Employees of the Month</h2>
                            <div className='ml-auto flex items-center gap-2'>
                                <label className='text-sm font-medium text-gray-600'>Month:</label>
                                <Calendar
                                    value={this.state.selectedWinnerMonth}
                                    onChange={this.handleMonthChange}
                                    view="month"
                                    dateFormat="MM yy"
                                    placeholder="Select Month"
                                    maxDate={new Date()}
                                    yearNavigator
                                    yearRange={`2020:${moment().year()}`}
                                    className="month-picker"
                                    style={{ width: '180px' }}
                                    showIcon
                                />
                            </div>
                        </div>

                        {isLoadingWinners ? (
                            <div className='flex items-center justify-center py-10'>
                                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                            </div>
                        ) : (
                            <>
                                {/* Tabs */}
                                <div className='tabs-container flex items-center gap-2 mb-4 border-b border-gray-200 pb-2 overflow-x-auto'>
                                    {winners.map((winner, index) => (
                                        <button
                                            key={index}
                                            onClick={() => this.handleTabChange(index)}
                                            className={`flex items-center gap-2 py-2 px-4 rounded-t-[8px] text-sm font-medium border-0 whitespace-nowrap transition-all ${
                                                activeTabIndex === index
                                                    ? 'bg-[#004AAD] text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            <FontAwesomeIcon icon={faUser} className='text-xs' />
                                            {winner.employeename || `Winner ${index + 1}`}
                                        </button>
                                    ))}
                                    <button
                                        onClick={this.addNewWinner}
                                        className='flex items-center gap-2 py-2 px-4 rounded-t-[8px] text-sm font-medium border-0 bg-green-100 text-green-700 hover:bg-green-200 whitespace-nowrap transition-all'
                                    >
                                        <FontAwesomeIcon icon={faPlus} className='text-xs' />
                                        Add Winner
                                    </button>
                                </div>

                                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                                    {/* Image Section */}
                                    <div className='image-section'>
                                        {/* Main Image Display */}
                                        <div className='main-image-container relative bg-gray-100 rounded-[12px] h-[300px] flex items-center justify-center overflow-hidden'>
                                            {allImages.length > 0 && currentImage ? (
                                                <>
                                                    <img
                                                        src={currentImage.type === 'existing'
                                                            ? `${APIURL.replace(/\/$/, '')}/uploads/eom_images/${currentImage.filename}`
                                                            : currentImage.preview
                                                        }
                                                        alt="EOM Winner"
                                                        className='max-w-full max-h-full object-contain'
                                                    />
                                                    {allImages.length > 1 && (
                                                        <>
                                                            <button
                                                                onClick={() => this.navigateImage(-1)}
                                                                className='absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center border-0 shadow'
                                                            >
                                                                <FontAwesomeIcon icon={faChevronLeft} />
                                                            </button>
                                                            <button
                                                                onClick={() => this.navigateImage(1)}
                                                                className='absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center border-0 shadow'
                                                            >
                                                                <FontAwesomeIcon icon={faChevronRight} />
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <div className='text-gray-400 text-center'>
                                                    <FontAwesomeIcon icon={faUpload} className='text-4xl mb-2' />
                                                    <p>No images selected</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Thumbnail Strip */}
                                        <div className='thumbnails-container mt-3 flex gap-2 overflow-x-auto pb-2'>
                                            {allImages.map((img, index) => (
                                                <div
                                                    key={index}
                                                    className={`relative flex-shrink-0 w-[70px] h-[70px] rounded-[8px] overflow-hidden cursor-pointer border-2 ${this.state.currentImageIndex === index ? 'border-blue-500' : 'border-transparent'}`}
                                                    onClick={() => this.setState({ currentImageIndex: index })}
                                                >
                                                    <img
                                                        src={img.type === 'existing'
                                                            ? `${APIURL.replace(/\/$/, '')}/uploads/eom_images/${img.filename}`
                                                            : img.preview
                                                        }
                                                        alt=""
                                                        className='w-full h-full object-contain'
                                                    />
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            img.type === 'existing'
                                                                ? this.removeExistingImage(img.index)
                                                                : this.removeTempImage(img.index);
                                                        }}
                                                        className='absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs border-0 flex items-center justify-center'
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} className='text-[10px]' />
                                                    </button>
                                                </div>
                                            ))}
                                            <label className='flex-shrink-0 w-[70px] h-[70px] rounded-[8px] border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50'>
                                                {this.state.isUploadingImages ? (
                                                    <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faUpload} className='text-gray-400' />
                                                )}
                                                <input
                                                    type='file'
                                                    accept='image/jpeg,image/jpg,image/png,image/gif'
                                                    multiple
                                                    onChange={this.handleImageSelect}
                                                    ref={this.fileInputRef}
                                                    className='hidden'
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    {/* Form Section */}
                                    <div className='form-section'>
                                        <div className='mb-4'>
                                            <label className='block text-sm font-medium text-gray-700 mb-1'>Employee Name *</label>
                                            <input
                                                type='text'
                                                value={currentWinner.employeename}
                                                onChange={(e) => this.handleWinnerFormChange('employeename', e.target.value)}
                                                placeholder='Enter employee name'
                                                className='w-full py-2 px-3 border border-gray-300 rounded-[8px] text-sm'
                                            />
                                        </div>
                                        <div className='mb-4'>
                                            <label className='block text-sm font-medium text-gray-700 mb-1'>Role & Service Area *</label>
                                            <input
                                                type='text'
                                                value={currentWinner.roleservicearea}
                                                onChange={(e) => this.handleWinnerFormChange('roleservicearea', e.target.value)}
                                                placeholder='e.g., Nurse, Oakwood House'
                                                className='w-full py-2 px-3 border border-gray-300 rounded-[8px] text-sm'
                                            />
                                        </div>
                                        <div className='mb-4'>
                                            <label className='block text-sm font-medium text-gray-700 mb-1'>Why They Were Selected *</label>
                                            <textarea
                                                value={currentWinner.description}
                                                onChange={(e) => this.handleWinnerFormChange('description', e.target.value)}
                                                placeholder='Short paragraph highlighting their contribution...'
                                                rows={5}
                                                className='w-full py-2 px-3 border border-gray-300 rounded-[8px] text-sm resize-none'
                                            />
                                        </div>
                                        <div className='flex items-center gap-3'>
                                            <button
                                                onClick={this.saveWinner}
                                                disabled={this.state.isSavingWinner}
                                                className='flex items-center gap-2 py-[10px] px-[20px] text-[14px] font-medium rounded-[8px] text-white bg-[#004AAD] hover:bg-[#003080] border-0 disabled:opacity-50'
                                            >
                                                {this.state.isSavingWinner ? (
                                                    <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" />
                                                ) : (
                                                    <FontAwesomeIcon icon={faSave} />
                                                )}
                                                {currentWinner.id ? 'Update Winner' : 'Save Winner'}
                                            </button>
                                            {(currentWinner.id || winners.length > 1) && (
                                                <button
                                                    onClick={this.deleteWinner}
                                                    disabled={this.state.isDeletingWinner}
                                                    className='flex items-center gap-2 py-[10px] px-[20px] text-[14px] font-medium rounded-[8px] text-white bg-red-500 hover:bg-red-600 border-0 disabled:opacity-50'
                                                >
                                                    {this.state.isDeletingWinner ? (
                                                        <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    )}
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
